<template>
  <Teleport to="body">
    <div v-if="isOpen" class="record-insights-overlay" @click="close"></div>

    <aside v-if="isOpen" class="record-insights-drawer" @click.stop>
      <header class="record-insights-header">
        <div>
          <h3 class="record-insights-title">
            <i class="bi bi-pie-chart text-primary me-2"></i>
            {{ label || 'Análise dos registros' }}
          </h3>
          <p class="record-insights-subtitle">
            O que se repete nos campos estruturados do prontuário
          </p>
        </div>
        <button type="button" class="btn btn-sm btn-icon btn-light" @click="close">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="record-insights-toolbar">
        <button
          v-for="option in periodOptions"
          :key="option.label"
          type="button"
          class="btn btn-sm"
          :class="months === option.value ? 'btn-primary' : 'btn-light'"
          @click="changePeriod(option.value)">
          {{ option.label }}
        </button>
      </div>

      <div class="record-insights-body">
        <div v-if="loading" class="record-insights-empty">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-3 mb-0">Carregando análise...</p>
        </div>

        <div v-else-if="!attributes.length" class="record-insights-empty">
          <i class="bi bi-clipboard-data fs-1 text-muted"></i>
          <p class="mt-3 mb-0">Nenhum registro preenchido neste período.</p>
        </div>

        <template v-else>
          <section v-for="attribute in attributes" :key="attribute.label" class="record-insights-section">
            <div class="d-flex justify-content-between align-items-baseline mb-3">
              <h4 class="record-insights-section-title mb-0">{{ attribute.label }}</h4>
              <span class="text-muted fs-8">
                {{ attribute.items }} registro{{ attribute.items === 1 ? '' : 's' }}
                em {{ attribute.records }} sessõe{{ attribute.records === 1 ? '' : 's' }}
              </span>
            </div>

            <div v-for="field in attribute.fields" :key="field.key" class="record-insights-field">
              <div class="d-flex justify-content-between align-items-baseline mb-2">
                <span class="fw-semibold fs-7 text-gray-800">{{ field.label }}</span>
                <span class="text-muted fs-8" v-if="field.kind === 'text'">
                  {{ field.distinct }} valores diferentes
                </span>
              </div>

              <div v-if="field.kind === 'number' && field.stats" class="record-insights-stats">
                <div v-for="stat in numberStats(field)" :key="stat.label">
                  <div class="fw-bold text-gray-800">{{ stat.value }}</div>
                  <div class="text-muted fs-8">{{ stat.label }}</div>
                </div>
              </div>

              <div v-else class="d-flex flex-column gap-2">
                <div v-for="item in field.values" :key="item.value" class="record-insights-bar-row">
                  <div class="d-flex justify-content-between fs-8 mb-1">
                    <span class="text-gray-800 text-truncate">{{ item.value }}</span>
                    <span class="text-muted">{{ item.count }} · {{ item.percent }}%</span>
                  </div>
                  <div class="record-insights-bar">
                    <div class="record-insights-bar-fill" :style="{ width: item.percent + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="record-insights-chart" v-if="attribute.series.length > 1">
              <apexchart
                type="line"
                height="180"
                :options="chartOptions(attribute)"
                :series="chartSeries(attribute)"/>
            </div>
          </section>
        </template>
      </div>
    </aside>
  </Teleport>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts/src/vue3-apexcharts';
import http from '../../../helpers/http';
import Alert from '../../../helpers/alert';

const PERIOD_OPTIONS = [
  { value: 3, label: '3 meses' },
  { value: 6, label: '6 meses' },
  { value: 12, label: '1 ano' },
  { value: null, label: 'Tudo' },
];

export default {
  name: 'RecordInsightsDrawer',
  components: { apexchart: VueApexCharts },
  props: {
    isOpen: { type: Boolean, default: false },
    professionalId: { type: [Number, String], default: null },
    patientId: { type: [Number, String], default: null },
    placeId: { type: [Number, String], default: null },
    label: { type: String, default: '' },
  },
  emits: ['close'],
  data() {
    return {
      months: 3,
      periodOptions: PERIOD_OPTIONS,
      loading: false,
      attributes: [],
    };
  },
  watch: {
    isOpen(open) {
      if (open) this.load();
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    changePeriod(value) {
      this.months = value;
      this.load();
    },
    load() {
      if (!this.professionalId || !this.patientId) return;
      this.loading = true;

      const params = {};
      if (this.months) params.months = this.months;
      if (this.placeId) params.id_place = this.placeId;
      if (this.label) params.label = this.label;

      http.get(`/professionals/${this.professionalId}/patients/${this.patientId}/record-insights`, { params })
        .then((response) => {
          this.attributes = response.data?.attributes || [];
        })
        .catch((e) => Alert.toastError(e.response?.data?.message ?? e.response?.data ?? e))
        .finally(() => { this.loading = false; });
    },
    numberStats(field) {
      return [
        { label: 'Média', value: field.stats.avg },
        { label: 'Mínimo', value: field.stats.min },
        { label: 'Máximo', value: field.stats.max },
        { label: 'Total', value: field.stats.sum },
      ];
    },
    chartSeries(attribute) {
      return [{
        name: 'Registros',
        data: attribute.series.map((point) => point.items),
      }];
    },
    chartOptions(attribute) {
      return {
        chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit' },
        colors: ['#009ef7'],
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 4, strokeWidth: 2 },
        dataLabels: { enabled: false },
        legend: { show: false },
        grid: { borderColor: '#eff2f5', strokeDashArray: 4 },
        title: {
          text: 'Registros por dia',
          style: { fontSize: '11px', color: '#a1a5b7', fontWeight: 500 },
        },
        xaxis: {
          categories: attribute.series.map((point) => {
            const [, month, day] = String(point.date).split('-');
            return `${day}/${month}`;
          }),
          labels: { style: { fontSize: '11px', colors: '#7e8299' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          labels: {
            style: { fontSize: '11px', colors: '#7e8299' },
            formatter: (value) => `${Math.round(value)}`,
          },
        },
      };
    },
  },
};
</script>

<style scoped>
.record-insights-overlay {
  position: fixed;
  inset: 0;
  background: rgba(24, 28, 50, 0.35);
  z-index: 1055;
}
.record-insights-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: min(520px, 100vw);
  height: 100vh;
  background: #fff;
  z-index: 1056;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 30px rgba(0, 0, 0, 0.12);
}
.record-insights-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 20px 12px;
  border-bottom: 1px solid #eff2f5;
}
.record-insights-title {
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0;
}
.record-insights-subtitle {
  margin: 4px 0 0;
  color: #7e8299;
  font-size: 0.85rem;
}
.record-insights-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 1px solid #eff2f5;
}
.record-insights-body {
  padding: 16px 20px 28px;
  overflow: auto;
  flex: 1;
}
.record-insights-empty {
  text-align: center;
  color: #7e8299;
  padding: 48px 12px;
}
.record-insights-section {
  margin-bottom: 28px;
}
.record-insights-section-title {
  font-size: 0.95rem;
  font-weight: 700;
}
.record-insights-field {
  border: 1px solid #eff2f5;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 10px;
}
.record-insights-bar {
  background: #f1f1f4;
  border-radius: 6px;
  height: 8px;
  overflow: hidden;
}
.record-insights-bar-fill {
  background: #7239ea;
  height: 100%;
  border-radius: 6px;
}
.record-insights-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.record-insights-chart {
  border: 1px solid #eff2f5;
  border-radius: 12px;
  padding: 8px 8px 0;
}
</style>
